/**
 * Every number both the server and the client need to agree on.
 * If a value lives here, neither side is allowed to keep its own copy.
 */
/** Authoritative simulation rate. The server advances the world exactly this often. */
export const TICK_RATE = 30;
export const TICK_MS = 1000 / TICK_RATE;
/** Fixed timestep, in seconds. Physics never sees a variable dt. */
export const FIXED_DT = 1 / TICK_RATE;
/** How far behind the newest snapshot the client renders remote players. */
export const INTERPOLATION_DELAY_MS = 100;
export const ROOM_NAME = "arena";
export const MAX_PLAYERS = 10;
/** Below this many players the match can't start (or pauses if it drops). */
export const MIN_PLAYERS = 2;
/**
 * The phases in which a match is actually under way. The room is closed to
 * anyone who walked out of one of these, and the pause menu offers the host
 * the restart / back-to-lobby exits only while the room is in one of them.
 */
export const LIVE_PHASES = ["countdown", "playing", "roundOver"];
/** Is the fight on? (As opposed to sitting in the lobby or on the end card.) */
export const isLivePhase = (phase) => LIVE_PHASES.includes(phase);
/**
 * A player's own id, kept in their browser and sent with every join.
 *
 * The session id is no use for this: it is new on every connection, so the
 * moment someone quits and comes back the server cannot tell it is them. This
 * is only ever used to hold a quitter out of the match they abandoned — it is
 * not an account, and a client is free to forge one.
 */
export const MAX_CLIENT_TOKEN_LENGTH = 64;
/**
 * Close code for "you left this match, you don't get to come back mid-fight".
 * Above 4000, which WebSocket reserves for the application; the client matches
 * on the message rather than the number, but a distinct code keeps the server
 * logs readable.
 */
export const ERR_LEFT_MID_MATCH = 4200;
/** What the server says when it turns a quitter away. The client shows this. */
export const LEFT_MID_MATCH_MESSAGE = "You left this match — you can rejoin when it ends.";
/** Default match rules. The host can change these from the lobby. */
export const TOTAL_ROUNDS = 3;
export const LIVES_PER_ROUND = 3;
/** What the host is allowed to pick, so the server can validate the message. */
export const ROUND_OPTIONS = [1, 3, 5, 7];
export const LIVES_OPTIONS = [1, 2, 3, 5];
/** Best-of: first to this many round wins takes the match. */
export const roundWinsToTakeMatch = (totalRounds) => Math.ceil(totalRounds / 2);
export const ROUND_WINS_TO_TAKE_MATCH = roundWinsToTakeMatch(TOTAL_ROUNDS);
/**
 * Room codes. Four characters from an alphabet with no I/O/0/1, because these
 * get read aloud across a noisy table. 24^4 ≈ 331k combinations.
 */
export const ROOM_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ";
export const ROOM_CODE_LENGTH = 4;
/** The stake is a joke tracker, never money. Kept short enough to read big. */
export const MAX_STAKE_LENGTH = 80;
export const MAX_NAME_LENGTH = 12;
export const DEFAULT_STAKE = "Loser buys the winner lunch";
/** Phase durations, in milliseconds. */
export const COUNTDOWN_MS = 3000;
export const ROUND_OVER_MS = 4000;
/** Dead but with lives left: this long face-down before respawning. */
export const RESPAWN_DELAY_MS = 1200;
/** Grace period after respawning during which you can't be killed or knocked. */
export const SPAWN_IFRAME_MS = 1500;
/**
 * How long an attack swing lasts, and the recovery before another is allowed.
 * Today this drives an animation only; Track B hangs the hitbox on the same
 * window, so the timing is already the thing that will matter.
 */
export const ATTACK_SWING_MS = 220;
export const ATTACK_RECOVERY_MS = 260;
/**
 * Within the swing: wind-up, then the frames that can actually connect. A
 * startup gap is what makes an attack a commitment rather than a free button.
 */
export const ATTACK_STARTUP_MS = 60;
export const ATTACK_ACTIVE_MS = 90;
/** Hitbox in front of the attacker, in arena units. */
export const ATTACK_REACH = 30;
export const ATTACK_BOX_HEIGHT = 36;
/**
 * Damage and knockback.
 *
 * There are no health bars — you die by leaving the arena. Damage is purely a
 * knockback multiplier: a fresh stickman barely budges, one at 120% flies. That
 * is what makes a comeback possible and what makes "he's at 140, don't let him
 * touch you" a real thing to shout across a table.
 */
export const HIT_DAMAGE = 9;
export const MAX_DAMAGE = 999;
/** Launch speed = base + damage × scaling, in px/s. */
export const KNOCKBACK_BASE = 200;
export const KNOCKBACK_SCALING = 4.2;
/** Every hit pops you up a little, and harder hits pop higher. */
export const KNOCKBACK_LIFT = 90;
export const KNOCKBACK_UP_RATIO = 0.42;
/** Hitstun: no control while you fly. Grows with damage, but capped. */
export const HITSTUN_BASE_MS = 180;
export const HITSTUN_PER_DAMAGE_MS = 1.6;
export const HITSTUN_MAX_MS = 700;
/**
 * All match timing is expressed in server ticks rather than wall-clock, so the
 * client can render every countdown from the synced `tick` field alone — no
 * clock alignment, no per-tick timer messages.
 */
export const msToTicks = (ms) => Math.round(ms / TICK_MS);
export const ticksToMs = (ticks) => ticks * TICK_MS;
/** Arena is a fixed-size world; the client letterboxes it into whatever screen it has. */
export const ARENA_WIDTH = 960;
export const ARENA_HEIGHT = 540;
/** Fall past this and you lose a life. */
export const KILL_PLANE_Y = ARENA_HEIGHT + 240;
/** One arena, one platform. Everything else is air. */
export const PLATFORMS = [
    { x: 90, y: 400, width: 780, height: 28 },
];
/**
 * Standing positions on the platform surface, not drop points above it —
 * players are frozen at spawn during the countdown, and a stickman hovering
 * in mid-air for three seconds looks broken.
 *
 * Ordered so that the first few players spread to the far corners and later
 * ones fill in between: a two-player match starts at opposite ends, not
 * shoulder to shoulder.
 */
export const SPAWN_POINTS = [
    { x: 150, y: 400 },
    { x: 810, y: 400 },
    { x: 330, y: 400 },
    { x: 630, y: 400 },
    { x: 240, y: 400 },
    { x: 720, y: 400 },
    { x: 480, y: 400 },
    { x: 390, y: 400 },
    { x: 570, y: 400 },
    { x: 285, y: 400 },
];
export const PLAYER_WIDTH = 22;
export const PLAYER_HEIGHT = 56;
/** Movement feel. Tuned for thumbs, not keyboards. */
export const MOVE_SPEED = 260;
export const GROUND_ACCEL = 2600;
export const AIR_ACCEL = 1400;
export const GROUND_FRICTION = 2200;
export const AIR_FRICTION = 260;
export const GRAVITY = 1900;
export const JUMP_VELOCITY = -640;
/** Releasing jump early cuts the rise, so taps are short hops. */
export const JUMP_CUT_MULTIPLIER = 0.45;
/**
 * How far a full-hold jump lifts the feet, in arena units — the hard ceiling on
 * a climbable step. It falls out of `JUMP_VELOCITY`, `GRAVITY` and the fixed
 * timestep (semi-implicit Euler, so a touch under the `v²/2g` analytic value);
 * measured, not derived, at ~97. Map tiers should sit well below it — aim for
 * ≤ 84 so a jump with any horizontal offset still lands — and `test/maps.mjs`
 * simulates real jumps between every platform pair and fails if one can't be
 * reached.
 */
export const MAX_JUMP_RISE = 97;
/** The comfortable step a map should design to, leaving headroom for a gap that is also horizontal. */
export const COMFORTABLE_JUMP_RISE = 84;
export const MAX_FALL_SPEED = 1300;
/** Ticks of grace after walking off a ledge during which jump still works. */
export const COYOTE_TICKS = 4;
/** Ticks a jump press stays buffered while airborne. */
export const JUMP_BUFFER_TICKS = 5;
/**
 * Stickman colours, handed out in join order. Ten of them, ordered so the
 * earliest joiners get the most distinguishable pairs — with ten sticks in a
 * scrum, telling yours apart is the whole game. This is only the *default*:
 * a player can override it from the wardrobe (see below).
 */
export const PLAYER_COLORS = [
    "#ff5a5f", // red
    "#4cc9f0", // cyan
    "#ffd166", // yellow
    "#8ce99a", // green
    "#c792ea", // violet
    "#ff9f45", // orange
    "#f78fb3", // pink
    "#6ee7d7", // teal
    "#b0bec5", // slate
    "#a3e635", // lime
];
/**
 * ------------------------------------------------------------------ wardrobe
 *
 * Player customisation. Purely cosmetic — none of this reaches `stepBody`, so
 * a skin or a hat can never change a hitbox or the physics. Both the picked
 * colour and the hat id ride in on the join options and can be changed later
 * with a `customize` message; the server validates every value against the
 * lists here rather than trusting the client.
 */
/** The swatches the wardrobe offers. A custom hex picker sits alongside these. */
export const WARDROBE_COLORS = [
    "#ff5a5f", // red
    "#ff9f45", // orange
    "#ffd166", // amber
    "#a3e635", // lime
    "#8ce99a", // green
    "#6ee7d7", // teal
    "#4cc9f0", // cyan
    "#5b8cff", // blue
    "#c792ea", // violet
    "#f78fb3", // pink
    "#e0a458", // tan
    "#b0bec5", // slate
    "#8d99ae", // steel
    "#2ec4b6", // jade
    "#f4f4f5", // white
    "#4a4e69", // ink
];
/** A six-digit `#rrggbb`. The wardrobe's custom picker only ever emits this. */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
/** Is this a colour we're willing to put on the wire and draw? */
export function isHexColor(value) {
    return typeof value === "string" && HEX_COLOR.test(value);
}
/** A valid `#rrggbb` (lower-cased), or the fallback if the input is junk. */
export function normalizeHexColor(value, fallback) {
    return isHexColor(value) ? value.toLowerCase() : fallback;
}
/**
 * The hats. `none` is a real option and the default. Each id is drawn by the
 * client renderer; the server only ever stores and validates the id.
 */
export const HATS = [
    { id: "none", label: "None" },
    { id: "top", label: "Top hat" },
    { id: "cap", label: "Cap" },
    { id: "beanie", label: "Beanie" },
    { id: "band", label: "Headband" },
    { id: "crown", label: "Crown" },
    { id: "party", label: "Party" },
    { id: "halo", label: "Halo" },
    { id: "horns", label: "Horns" },
    { id: "antenna", label: "Antenna" },
];
export const DEFAULT_HAT = "none";
const HAT_IDS = new Set(HATS.map((hat) => hat.id));
/** Is this one of the hats we know how to draw? */
export function isHatId(value) {
    return typeof value === "string" && HAT_IDS.has(value);
}
//# sourceMappingURL=constants.js.map